import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {
    Button,
    Card,
    Col,
    Flex,
    Form,
    Image,
    Input,
    InputNumber,
    message,
    Row,
    Segmented,
    Select,
    Spin,
    Switch,
    Typography,
    Upload
} from "antd";
import {ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, UploadOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import MarketCourseService from "../../../../../services/MarketCourseService";
import {AUDIENCES, CATEGORIES, FORMATS, LEVELS} from "../../../../Courses/courses.enums";
import LocalizedInput from "../components/LocalizedInput";
import ListRowActions from "../components/ListRowActions";
import {
    buildPayload,
    emptyCourseValues,
    LANG_CODES,
    LANGS,
    SLUG_PATTERN,
    toFormValues
} from "../marketCourse.utils";

const {Title, Text} = Typography;

/** Максимальный размер фото преподавателя. Больше на карточке всё равно не нужно. */
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

/**
 * Создание и редактирование курса витрины корпоративного обучения.
 *
 * Все текстовые поля — триплеты {ru, kk, en}, но три инпута подряд под каждой подписью
 * превратили бы форму в простыню, поэтому язык ввода переключается один раз на всю форму
 * (Segmented сверху). Значения всех трёх языков живут в состоянии формы одновременно
 * и уходят на бэкенд целиком — подробности в LocalizedInput.
 */
const MarketCourseForm = ({isEdit = false}) => {
    const navigate = useNavigate();
    const {id} = useParams();
    const [form] = Form.useForm();
    // Одна подписка на переводы на весь компонент: строки нужны в options селектов
    // и в атрибутах title, а хук нельзя звать внутри .map и обработчиков.
    const {t} = useTranslation();

    /** Язык, который сейчас редактируется. Общий для всех полей формы. */
    const [lang, setLang] = useState('ru');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    // Преподаватель формой не создаётся заново: его id тянем из загруженного курса.

    const photo = Form.useWatch(['trainer', 'photo'], form);
    // Считаем один раз: Form читает initialValues только при монтировании,
    // а новый объект на каждый рендер зря переписывал бы ссылку в форме.
    const initialValues = useMemo(() => emptyCourseValues(), []);

    useEffect(() => {
        if (!isEdit || !id) {
            return undefined;
        }
        let isCancelled = false;
        setIsLoading(true);
        MarketCourseService.getById(id)
            .then((course) => {
                if (isCancelled) {
                    return;
                }
                form.setFieldsValue(toFormValues(course));
            })
            .catch(() => {
                if (!isCancelled) {
                    message.error(<IntlMessage id={'marketCoursesPage.courseLoadFailed'}/>, 5);
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            isCancelled = true;
        };
    }, [isEdit, id, form]);

    /** Подписи словарных значений берём из courses.enums.js, второго списка кодов не заводим. */
    const enumOptions = (dictionary) => dictionary.map(({code, labelKey}) => ({value: code, label: t(labelKey)}));

    /**
     * n модуля показывается на витрине как его номер, поэтому после перестановки
     * и удаления строк пересчитываем нумерацию по фактическому порядку.
     */
    const renumberModules = () => {
        const modules = form.getFieldValue('modules') ?? [];
        form.setFieldValue('modules', modules.map((module, index) => ({...module, n: index + 1})));
    };

    async function onFinish(values) {
        setIsSaving(true);
        try {
            await MarketCourseService.save(buildPayload(values, {id: isEdit ? id : null}));
            message.success(<IntlMessage id={'marketCoursesPage.saved'}/>, 5);
            navigate('/p/market-courses');
        } catch (error) {
            message.error(<IntlMessage id={'marketCoursesPage.saveFailed'}/>, 5);
        } finally {
            setIsSaving(false);
        }
    }

    /**
     * Незаполненное обязательное поле может оказаться на скрытой сейчас вкладке языка —
     * подсветка была бы не видна, поэтому переключаемся на язык первой ошибки.
     */
    function onFinishFailed({errorFields}) {
        const failedLang = (errorFields ?? [])
            .map((field) => field.name?.[field.name.length - 1])
            .find((lastPathPart) => LANG_CODES.includes(lastPathPart));
        if (failedLang) {
            setLang(failedLang);
        }
        message.error(<IntlMessage id={'marketCoursesPage.validationFailed'}/>, 5);
    }

    function beforeUploadPhoto(file) {
        if (!file.type?.startsWith('image/')) {
            message.error(<IntlMessage id={'marketCoursesPage.photoTypeError'}/>, 5);
            return Upload.LIST_IGNORE;
        }
        if (file.size > MAX_PHOTO_SIZE) {
            message.error(<IntlMessage id={'marketCoursesPage.photoSizeError'}/>, 5);
            return Upload.LIST_IGNORE;
        }
        return true;
    }

    /**
     * customRequest: antd не должен слать файл сам — загрузку делает MarketCourseService,
     * он отдаёт имя файла, которое и есть значение trainer.photo.
     */
    async function uploadPhoto({file, onSuccess, onError}) {
        setIsUploading(true);
        try {
            const fileName = await MarketCourseService.uploadPhoto(file);
            form.setFieldValue(['trainer', 'photo'], fileName);
            message.success(<IntlMessage id={'marketCoursesPage.photoUploaded'}/>, 5);
            onSuccess?.(fileName);
        } catch (error) {
            message.error(<IntlMessage id={'marketCoursesPage.photoFailed'}/>, 5);
            onError?.(error);
        } finally {
            setIsUploading(false);
        }
    }

    return (
        <div style={{width: '100%', height: '100%'}}>
            <Card bordered={false} style={{height: '100%'}}>
                <Flex justify={"space-between"} align={"center"} gap={20} wrap={"wrap"} style={{marginBottom: 16}}>
                    <Flex align={"center"} gap={12}>
                        <Button icon={<ArrowLeftOutlined/>} onClick={() => navigate('/p/market-courses')}>
                            <IntlMessage id={'back'}/>
                        </Button>
                        <Title level={3} style={{margin: 0}}>
                            <IntlMessage id={isEdit ? 'marketCoursesPage.editTitle' : 'marketCoursesPage.createTitle'}/>
                        </Title>
                    </Flex>
                    <Button
                        type={"primary"}
                        icon={<SaveOutlined/>}
                        size={"large"}
                        loading={isSaving}
                        onClick={() => form.submit()}
                    >
                        <IntlMessage id={'save'}/>
                    </Button>
                </Flex>

                <Flex vertical gap={4} style={{marginBottom: 16}}>
                    <Segmented
                        value={lang}
                        onChange={setLang}
                        options={LANGS.map(({code, labelKey}) => ({value: code, label: t(labelKey)}))}
                    />
                    <Text type={"secondary"} style={{fontSize: 12}}>
                        <IntlMessage id={'marketCoursesPage.langHint'}/>
                    </Text>
                </Flex>

                <Spin spinning={isLoading}>
                    <Form
                        form={form}
                        layout={"vertical"}
                        initialValues={initialValues}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        style={{width: '100%'}}
                    >
                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.general'}/>}
                            style={{marginBottom: 16}}
                        >
                            <Row gutter={16}>
                                <Col xs={24} md={10}>
                                    <Form.Item
                                        name={"slug"}
                                        label={<IntlMessage id={'marketCoursesPage.field.slug'}/>}
                                        style={{marginBottom: 12}}
                                        rules={[
                                            {
                                                required: true,
                                                message: <IntlMessage id={'marketCoursesPage.required'}/>
                                            },
                                            {
                                                pattern: SLUG_PATTERN,
                                                message: <IntlMessage id={'marketCoursesPage.slugPattern'}/>
                                            },
                                        ]}
                                    >
                                        <Input placeholder={"ai-v-processah-hr"}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={7}>
                                    <Form.Item
                                        name={"sortOrder"}
                                        label={<IntlMessage id={'marketCoursesPage.field.sortOrder'}/>}
                                        style={{marginBottom: 12}}
                                        rules={[{
                                            required: true,
                                            message: <IntlMessage id={'marketCoursesPage.required'}/>
                                        }]}
                                    >
                                        <InputNumber min={0} style={{width: '100%'}}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={7}>
                                    <Form.Item
                                        name={"visible"}
                                        label={<IntlMessage id={'marketCoursesPage.field.visible'}/>}
                                        valuePropName={"checked"}
                                        style={{marginBottom: 12}}
                                    >
                                        <Switch/>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name={"category"}
                                        label={<IntlMessage id={'marketCoursesPage.field.category'}/>}
                                        style={{marginBottom: 12}}
                                        rules={[{
                                            required: true,
                                            message: <IntlMessage id={'marketCoursesPage.required'}/>
                                        }]}
                                    >
                                        <Select options={enumOptions(CATEGORIES)}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name={"format"}
                                        label={<IntlMessage id={'marketCoursesPage.field.format'}/>}
                                        style={{marginBottom: 12}}
                                        rules={[{
                                            required: true,
                                            message: <IntlMessage id={'marketCoursesPage.required'}/>
                                        }]}
                                    >
                                        <Select options={enumOptions(FORMATS)}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        name={"level"}
                                        label={<IntlMessage id={'marketCoursesPage.field.level'}/>}
                                        style={{marginBottom: 12}}
                                        rules={[{
                                            required: true,
                                            message: <IntlMessage id={'marketCoursesPage.required'}/>
                                        }]}
                                    >
                                        <Select options={enumOptions(LEVELS)}/>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item
                                name={"audienceTags"}
                                label={<IntlMessage id={'marketCoursesPage.field.audienceTags'}/>}
                                style={{marginBottom: 0}}
                            >
                                <Select mode={"multiple"} allowClear options={enumOptions(AUDIENCES)}/>
                            </Form.Item>
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.texts'}/>}
                            style={{marginBottom: 16}}
                        >
                            <LocalizedInput
                                name={"title"}
                                lang={lang}
                                required
                                label={<IntlMessage id={'marketCoursesPage.field.title'}/>}
                            />
                            <LocalizedInput
                                name={"subtitle"}
                                lang={lang}
                                rows={2}
                                label={<IntlMessage id={'marketCoursesPage.field.subtitle'}/>}
                            />
                            <LocalizedInput
                                name={"cardDescription"}
                                lang={lang}
                                rows={3}
                                label={<IntlMessage id={'marketCoursesPage.field.cardDescription'}/>}
                            />
                            <LocalizedInput
                                name={"goal"}
                                lang={lang}
                                rows={3}
                                label={<IntlMessage id={'marketCoursesPage.field.goal'}/>}
                            />
                            <LocalizedInput
                                name={"finalProduct"}
                                lang={lang}
                                rows={2}
                                label={<IntlMessage id={'marketCoursesPage.field.finalProduct'}/>}
                            />
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.numbers'}/>}
                            style={{marginBottom: 16}}
                        >
                            <Row gutter={16}>
                                <Col xs={12} md={6}>
                                    <Form.Item
                                        name={"durationHours"}
                                        label={<IntlMessage id={'marketCoursesPage.field.durationHours'}/>}
                                        style={{marginBottom: 12}}
                                        rules={[{
                                            required: true,
                                            message: <IntlMessage id={'marketCoursesPage.required'}/>
                                        }]}
                                    >
                                        <InputNumber min={0} step={0.5} style={{width: '100%'}}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Item
                                        name={"priceTenge"}
                                        label={<IntlMessage id={'marketCoursesPage.field.priceTenge'}/>}
                                        style={{marginBottom: 12}}
                                    >
                                        <InputNumber min={0} step={1000} style={{width: '100%'}}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Item
                                        name={"minGroupSize"}
                                        label={<IntlMessage id={'marketCoursesPage.field.minGroupSize'}/>}
                                        style={{marginBottom: 12}}
                                    >
                                        <InputNumber min={0} style={{width: '100%'}}/>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Item
                                        name={"corporateFromSize"}
                                        label={<IntlMessage id={'marketCoursesPage.field.corporateFromSize'}/>}
                                        style={{marginBottom: 12}}
                                    >
                                        <InputNumber min={0} style={{width: '100%'}}/>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <LocalizedInput
                                name={"durationShape"}
                                lang={lang}
                                label={<IntlMessage id={'marketCoursesPage.field.durationShape'}/>}
                            />
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.trainer'}/>}
                            style={{marginBottom: 16}}
                        >
                            <LocalizedInput
                                name={["trainer", "name"]}
                                lang={lang}
                                label={<IntlMessage id={'marketCoursesPage.field.trainerName'}/>}
                            />
                            <LocalizedInput
                                name={["trainer", "position"]}
                                lang={lang}
                                label={<IntlMessage id={'marketCoursesPage.field.trainerPosition'}/>}
                            />
                            <LocalizedInput
                                name={["trainer", "credentials"]}
                                lang={lang}
                                rows={3}
                                label={<IntlMessage id={'marketCoursesPage.field.trainerCredentials'}/>}
                            />
                            <LocalizedInput
                                name={["trainer", "why"]}
                                lang={lang}
                                rows={3}
                                label={<IntlMessage id={'marketCoursesPage.field.trainerWhy'}/>}
                            />
                            <Form.Item
                                name={["trainer", "isPlaceholder"]}
                                label={<IntlMessage id={'marketCoursesPage.field.trainerIsPlaceholder'}/>}
                                valuePropName={"checked"}
                                style={{marginBottom: 12}}
                            >
                                <Switch/>
                            </Form.Item>
                            {/* Значение поля — имя файла с бэкенда; сам input скрыт,
                                видимая часть ниже: кнопка загрузки и превью. */}
                            <Form.Item name={["trainer", "photo"]} hidden>
                                <Input/>
                            </Form.Item>
                            <Form.Item
                                label={<IntlMessage id={'marketCoursesPage.field.trainerPhoto'}/>}
                                style={{marginBottom: 0}}
                            >
                                <Flex align={"center"} gap={16} wrap={"wrap"}>
                                    {photo && (
                                        <Image
                                            src={MarketCourseService.photoUrl(photo)}
                                            width={96}
                                            height={96}
                                            style={{objectFit: 'cover', borderRadius: 8}}
                                        />
                                    )}
                                    <Flex vertical gap={8} align={"flex-start"}>
                                        <Upload
                                            accept={"image/*"}
                                            maxCount={1}
                                            showUploadList={false}
                                            beforeUpload={beforeUploadPhoto}
                                            customRequest={uploadPhoto}
                                        >
                                            <Button icon={<UploadOutlined/>} loading={isUploading}>
                                                <IntlMessage id={'marketCoursesPage.uploadPhoto'}/>
                                            </Button>
                                        </Upload>
                                        {photo && (
                                            <Button
                                                type={"text"}
                                                danger
                                                icon={<DeleteOutlined/>}
                                                onClick={() => form.setFieldValue(['trainer', 'photo'], null)}
                                            >
                                                <IntlMessage id={'marketCoursesPage.removePhoto'}/>
                                            </Button>
                                        )}
                                    </Flex>
                                </Flex>
                            </Form.Item>
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.modules'}/>}
                            style={{marginBottom: 16}}
                        >
                            <Form.List name={"modules"}>
                                {(fields, {add, remove, move}) => (
                                    <Flex vertical gap={12} style={{width: '100%'}}>
                                        {fields.map((field, index) => (
                                            <Card
                                                key={field.key}
                                                size={"small"}
                                                type={"inner"}
                                                title={<IntlMessage id={'marketCoursesPage.moduleNumber'}
                                                                    values={{n: index + 1}}/>}
                                                extra={<ListRowActions
                                                    index={index}
                                                    total={fields.length}
                                                    onMove={(from, to) => {
                                                        move(from, to);
                                                        renumberModules();
                                                    }}
                                                    onRemove={(removeIndex) => {
                                                        remove(removeIndex);
                                                        renumberModules();
                                                    }}
                                                />}
                                            >
                                                <Row gutter={16}>
                                                    <Col xs={12} md={4}>
                                                        <Form.Item
                                                            name={[field.name, 'n']}
                                                            label={<IntlMessage
                                                                id={'marketCoursesPage.field.moduleN'}/>}
                                                            style={{marginBottom: 12}}
                                                        >
                                                            <InputNumber min={1} style={{width: '100%'}}/>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={12} md={4}>
                                                        <Form.Item
                                                            name={[field.name, 'hours']}
                                                            label={<IntlMessage
                                                                id={'marketCoursesPage.field.moduleHours'}/>}
                                                            style={{marginBottom: 12}}
                                                        >
                                                            <InputNumber min={0} step={0.5} style={{width: '100%'}}/>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} md={16}>
                                                        <LocalizedInput
                                                            name={[field.name, 'title']}
                                                            lang={lang}
                                                            label={<IntlMessage
                                                                id={'marketCoursesPage.field.moduleTitle'}/>}
                                                        />
                                                    </Col>
                                                </Row>
                                                <LocalizedInput
                                                    name={[field.name, 'content']}
                                                    lang={lang}
                                                    rows={3}
                                                    label={<IntlMessage
                                                        id={'marketCoursesPage.field.moduleContent'}/>}
                                                />
                                            </Card>
                                        ))}
                                        <Button
                                            type={"dashed"}
                                            icon={<PlusOutlined/>}
                                            onClick={() => add({n: fields.length + 1, hours: 0})}
                                        >
                                            <IntlMessage id={'marketCoursesPage.addModule'}/>
                                        </Button>
                                    </Flex>
                                )}
                            </Form.List>
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.outcomes'}/>}
                            style={{marginBottom: 16}}
                        >
                            <Form.List name={"outcomes"}>
                                {(fields, {add, remove, move}) => (
                                    <Flex vertical gap={12} style={{width: '100%'}}>
                                        {fields.map((field, index) => (
                                            <Card
                                                key={field.key}
                                                size={"small"}
                                                type={"inner"}
                                                title={<IntlMessage id={'marketCoursesPage.outcomeNumber'}
                                                                    values={{n: index + 1}}/>}
                                                extra={<ListRowActions
                                                    index={index}
                                                    total={fields.length}
                                                    onMove={move}
                                                    onRemove={remove}
                                                />}
                                            >
                                                <LocalizedInput
                                                    name={[field.name, 'title']}
                                                    lang={lang}
                                                    label={<IntlMessage
                                                        id={'marketCoursesPage.field.outcomeTitle'}/>}
                                                />
                                                <LocalizedInput
                                                    name={[field.name, 'text']}
                                                    lang={lang}
                                                    rows={2}
                                                    label={<IntlMessage
                                                        id={'marketCoursesPage.field.outcomeText'}/>}
                                                />
                                            </Card>
                                        ))}
                                        <Button type={"dashed"} icon={<PlusOutlined/>} onClick={() => add()}>
                                            <IntlMessage id={'marketCoursesPage.addOutcome'}/>
                                        </Button>
                                    </Flex>
                                )}
                            </Form.List>
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.targetAudience'}/>}
                            style={{marginBottom: 16}}
                        >
                            <Form.List name={"targetAudience"}>
                                {(fields, {add, remove, move}) => (
                                    <Flex vertical gap={4} style={{width: '100%'}}>
                                        {fields.map((field, index) => (
                                            <Flex key={field.key} align={"flex-start"} gap={8} style={{width: '100%'}}>
                                                <div style={{flex: 1}}>
                                                    {/* строка списка — сам по себе триплет, без вложенного ключа */}
                                                    <LocalizedInput name={[field.name]} lang={lang}/>
                                                </div>
                                                <ListRowActions
                                                    index={index}
                                                    total={fields.length}
                                                    onMove={move}
                                                    onRemove={remove}
                                                />
                                            </Flex>
                                        ))}
                                        <Button type={"dashed"} icon={<PlusOutlined/>} onClick={() => add()}>
                                            <IntlMessage id={'marketCoursesPage.addRow'}/>
                                        </Button>
                                    </Flex>
                                )}
                            </Form.List>
                        </Card>

                        <Card
                            size={"small"}
                            title={<IntlMessage id={'marketCoursesPage.section.forBusiness'}/>}
                            style={{marginBottom: 16}}
                        >
                            <Form.List name={"forBusiness"}>
                                {(fields, {add, remove, move}) => (
                                    <Flex vertical gap={4} style={{width: '100%'}}>
                                        {fields.map((field, index) => (
                                            <Flex key={field.key} align={"flex-start"} gap={8} style={{width: '100%'}}>
                                                <div style={{flex: 1}}>
                                                    <LocalizedInput name={[field.name]} lang={lang} rows={2}/>
                                                </div>
                                                <ListRowActions
                                                    index={index}
                                                    total={fields.length}
                                                    onMove={move}
                                                    onRemove={remove}
                                                />
                                            </Flex>
                                        ))}
                                        <Button type={"dashed"} icon={<PlusOutlined/>} onClick={() => add()}>
                                            <IntlMessage id={'marketCoursesPage.addRow'}/>
                                        </Button>
                                    </Flex>
                                )}
                            </Form.List>
                        </Card>

                        <Card size={"small"} title={<IntlMessage id={'marketCoursesPage.section.faq'}/>}>
                            <Form.List name={"faq"}>
                                {(fields, {add, remove, move}) => (
                                    <Flex vertical gap={12} style={{width: '100%'}}>
                                        {fields.map((field, index) => (
                                            <Card
                                                key={field.key}
                                                size={"small"}
                                                type={"inner"}
                                                title={<IntlMessage id={'marketCoursesPage.faqNumber'}
                                                                    values={{n: index + 1}}/>}
                                                extra={<ListRowActions
                                                    index={index}
                                                    total={fields.length}
                                                    onMove={move}
                                                    onRemove={remove}
                                                />}
                                            >
                                                <LocalizedInput
                                                    name={[field.name, 'q']}
                                                    lang={lang}
                                                    label={<IntlMessage
                                                        id={'marketCoursesPage.field.faqQuestion'}/>}
                                                />
                                                <LocalizedInput
                                                    name={[field.name, 'a']}
                                                    lang={lang}
                                                    rows={3}
                                                    label={<IntlMessage id={'marketCoursesPage.field.faqAnswer'}/>}
                                                />
                                            </Card>
                                        ))}
                                        <Button type={"dashed"} icon={<PlusOutlined/>} onClick={() => add()}>
                                            <IntlMessage id={'marketCoursesPage.addFaq'}/>
                                        </Button>
                                    </Flex>
                                )}
                            </Form.List>
                        </Card>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
};

export default MarketCourseForm;
